import { TagBadge } from '@features/allocation';
import {
  ActionMenu,
  Badge,
  Button,
  EmptyState,
  IconButton,
  Table,
  type TableSortDirection,
} from '@ntconsult/aluma-react';
import { Check, MoreVertical, Pencil, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AllocationTagColumnsState } from '../hooks/useAllocationTagColumns';
import type { AllocationTagFilterState } from '../hooks/useAllocationTagFilters';
import type { AllocationTag, AllocationTagSortField } from '../types/AllocationTag';

type AllocationTagsTableProps = {
  items: AllocationTag[];
  filterState: AllocationTagFilterState;
  columnsState: AllocationTagColumnsState;
  onEdit: (tag: AllocationTag) => void;
  onDelete: (tag: AllocationTag) => void;
};

/** Colunas e filtros do `AlocacaoTagHeaderList.php` do legado, mais as alocações vinculadas. */
export function AllocationTagsTable({
  items,
  filterState,
  columnsState,
  onEdit,
  onDelete,
}: AllocationTagsTableProps) {
  const { t } = useTranslation(['tagsAlocacao', 'alocacao', 'common']);
  const { filters, setSort, setSearch, setActive } = filterState;
  const { isVisible, visibleColumns } = columnsState;
  // +2: "Nome" e "Ações", sempre visíveis.
  const columnCount = visibleColumns.length + 2;

  const sortProps = (field: AllocationTagSortField) => ({
    sortable: true,
    sortDirection: (filters.sortBy === field
      ? filters.sortDirection
      : 'none') as TableSortDirection,
    onSortChange: (next: TableSortDirection) => setSort(field, next),
  });

  const activeOptions = [
    { value: 'true', label: t('common:yes') },
    { value: 'false', label: t('common:no') },
  ];

  return (
    <Table density="compact" stickyHeader containerClassName="overflow-visible">
      <Table.Caption className="sr-only">{t('tagsAlocacao:titulo')}</Table.Caption>
      <Table.Head>
        <Table.Row>
          <Table.Header
            {...sortProps('name')}
            filter={
              <Table.TextFilter
                label={t('tagsAlocacao:colunas.nome')}
                value={filters.name ?? ''}
                onValueChange={(value) => setSearch('name', value)}
              />
            }
          >
            {t('tagsAlocacao:colunas.nome')}
          </Table.Header>
          {isVisible('color') && (
            <Table.Header
              {...sortProps('color')}
              filter={
                <Table.TextFilter
                  label={t('tagsAlocacao:colunas.cor')}
                  value={filters.color ?? ''}
                  onValueChange={(value) => setSearch('color', value)}
                />
              }
            >
              {t('tagsAlocacao:colunas.cor')}
            </Table.Header>
          )}
          {isVisible('active') && (
            <Table.Header
              align="center"
              {...sortProps('active')}
              filter={
                <Table.OptionsFilter
                  label={t('tagsAlocacao:colunas.ativa')}
                  options={activeOptions}
                  value={filters.active === undefined ? [] : [String(filters.active)]}
                  // Sim e Não marcados juntos = sem filtro.
                  onValueChange={(values) =>
                    setActive(values.length === 1 ? values[0] === 'true' : null)
                  }
                />
              }
            >
              {t('tagsAlocacao:colunas.ativa')}
            </Table.Header>
          )}
          {isVisible('allocationCount') && (
            <Table.Header
              align="right"
              className="whitespace-nowrap"
              {...sortProps('allocationCount')}
            >
              {t('tagsAlocacao:colunas.alocacoes')}
            </Table.Header>
          )}
          <Table.Header align="right">
            <span className="sr-only">{t('alocacao:tabela.acoes')}</span>
          </Table.Header>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {items.length === 0 ? (
          // Vazio por filtro mantém o cabeçalho: é nele que o filtro se ajusta.
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <EmptyState
                title={t('tagsAlocacao:tabela.filtroSemResultadoTitulo')}
                description={t('tagsAlocacao:tabela.filtroSemResultadoDescricao')}
                action={
                  <Button variant="secondary" onClick={filterState.clearFilters}>
                    {t('alocacao:filtros.limpar')}
                  </Button>
                }
              />
            </Table.Cell>
          </Table.Row>
        ) : (
          items.map((tag) => (
            <Table.Row key={tag.id}>
              <Table.Cell>
                <TagBadge tag={tag} />
              </Table.Cell>
              {isVisible('color') && (
                <Table.Cell>
                  <span className="inline-flex items-center gap-2 font-mono text-sm">
                    {/* Cor é dado do Axiis (hex livre), não token — mesmo critério do `TagBadge`. */}
                    <span
                      aria-hidden="true"
                      className="inline-block h-4 w-4 shrink-0 rounded-sm border border-border"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.color}
                  </span>
                </Table.Cell>
              )}
              {isVisible('active') && (
                <Table.Cell align="center">
                  {tag.active ? (
                    <Badge
                      variant="soft"
                      tone="success"
                      shape="pill"
                      size="sm"
                      icon={<Check size={12} />}
                    >
                      {t('common:yes')}
                    </Badge>
                  ) : (
                    <Badge
                      variant="soft"
                      tone="danger"
                      shape="pill"
                      size="sm"
                      icon={<X size={12} />}
                    >
                      {t('common:no')}
                    </Badge>
                  )}
                </Table.Cell>
              )}
              {isVisible('allocationCount') && (
                <Table.Cell align="right" className="tabular-nums">
                  {tag.allocationCount}
                </Table.Cell>
              )}
              <Table.Cell align="right">
                <ActionMenu
                  align="right"
                  trigger={
                    <IconButton
                      variant="icon"
                      size="sm"
                      icon={<MoreVertical size={16} />}
                      label={t('tagsAlocacao:tabela.acoesDaTag', { nome: tag.name })}
                    />
                  }
                >
                  <ActionMenu.Item icon={<Pencil size={16} />} onClick={() => onEdit(tag)}>
                    {t('tagsAlocacao:acoes.editar')}
                  </ActionMenu.Item>
                  <ActionMenu.Item
                    variant="danger"
                    icon={<Trash2 size={16} />}
                    onClick={() => onDelete(tag)}
                  >
                    {t('tagsAlocacao:acoes.excluir')}
                  </ActionMenu.Item>
                </ActionMenu>
              </Table.Cell>
            </Table.Row>
          ))
        )}
      </Table.Body>
    </Table>
  );
}
